import { useTranslation } from '@spcc/core'
import {
  Table,
  TableBody,
  TableHeader,
  TableRow,
  TableCell,
  Badge,
  cn,
} from '@spcc/uikit'
import type { SapSyncJob, SyncStatus } from '../../domain/types'
import { SAP_SYNC_STATUS } from '../../domain/constants'
import { formatDuration } from '../utils/formatters'
import { SortableHeader } from './sortable-header'

type SortField = 'direction' | 'entityType' | 'sapOperation' | 'jobStatus' | 'durationMs' | 'createdAt'
type SortOrder = 'asc' | 'desc'

interface SyncJobsTableProps {
  jobs: SapSyncJob[]
  selectedRowId: string | null
  sortField?: SortField
  sortOrder?: SortOrder
  onSort?: (field: SortField) => void
  onRowClick: (job: SapSyncJob) => void
}

const STATUS_VARIANT: Record<SyncStatus, string> = {
  [SAP_SYNC_STATUS.COMPLETED]: 'bg-green-100 text-green-800',
  [SAP_SYNC_STATUS.PROCESSING]: 'bg-blue-100 text-blue-800',
  [SAP_SYNC_STATUS.PENDING]: 'bg-gray-100 text-gray-800',
  [SAP_SYNC_STATUS.FAILED]: 'bg-red-100 text-red-800',
  [SAP_SYNC_STATUS.DLQ]: 'bg-orange-100 text-orange-800',
}

interface ColumnConfig {
  key: string
  labelKey: string
  sortKey?: SortField
  align?: 'start' | 'center' | 'end'
}

function SyncJobRow({
  job,
  isSelected,
  onRowClick,
}: {
  job: SapSyncJob
  isSelected: boolean
  onRowClick: (job: SapSyncJob) => void
}) {
  return (
    <TableRow
      className={cn(
        'cursor-pointer border-border/40 transition-colors',
        isSelected ? 'bg-primary/5 border-primary/20' : 'hover:bg-muted/50',
      )}
      onClick={() => onRowClick(job)}
    >
      <TableCell className="px-5 py-3">
        <Badge variant="outline" className="text-xs capitalize">
          {job.direction}
        </Badge>
      </TableCell>
      <TableCell className="px-5 py-3 text-xs">{job.entityType}</TableCell>
      <TableCell className="px-5 py-3 font-mono text-xs">{job.entityId}</TableCell>
      <TableCell className="px-5 py-3 text-xs">{job.sapOperation || '-'}</TableCell>
      <TableCell className="px-5 py-3">
        <Badge className={cn('text-xs', STATUS_VARIANT[job.status])}>{job.status}</Badge>
      </TableCell>
      <TableCell className="px-5 py-3 font-mono text-xs">
        {job.responseStatus != null ? (
          <Badge className={cn('text-xs', job.responseStatus >= 200 && job.responseStatus < 300 ? 'bg-green-100 text-green-800' : job.responseStatus >= 400 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800')}>
            {job.responseStatus}
          </Badge>
        ) : '-'}
      </TableCell>
      <TableCell className="px-5 py-3 font-mono text-xs">{job.sapDocumentNumber || '-'}</TableCell>
      <TableCell className="px-5 py-3 text-xs text-muted-foreground">{formatDuration(job.durationMs)}</TableCell>
      <TableCell className="px-5 py-3 text-center">{job.attempts}</TableCell>
      <TableCell className="px-5 py-3 text-xs text-muted-foreground">
        {new Date(job.createdAt).toLocaleString()}
      </TableCell>
    </TableRow>
  )
}

export function SyncJobsTable({
  jobs,
  selectedRowId,
  sortField,
  sortOrder,
  onSort,
  onRowClick,
}: SyncJobsTableProps) {
  const { t } = useTranslation()

  const columns: ColumnConfig[] = [
    { key: 'direction', labelKey: 'sapSync.direction', sortKey: 'direction' },
    { key: 'entityType', labelKey: 'sapSync.entityType', sortKey: 'entityType' },
    { key: 'entityId', labelKey: 'sapSync.entityId' },
    { key: 'sapOperation', labelKey: 'sapSync.sapOperation', sortKey: 'sapOperation' },
    { key: 'status', labelKey: 'sapSync.status', sortKey: 'jobStatus' },
    { key: 'responseStatus', labelKey: 'sapSync.responseStatus' },
    { key: 'sapDoc', labelKey: 'sapSync.sapDoc' },
    { key: 'durationMs', labelKey: 'sapSync.executionTime', sortKey: 'durationMs' },
    { key: 'attempts', labelKey: 'sapSync.attempts', align: 'center' as const },
    { key: 'createdAt', labelKey: 'sapSync.createdAt', sortKey: 'createdAt' },
  ]

  return (
    <div className="glass-card rounded-2xl overflow-hidden border border-border/50">
      <Table>
        <TableHeader>
          <TableRow className="border-border/40 hover:bg-transparent bg-muted/50">
            {columns.map((col) => (
              <SortableHeader
                key={col.key}
                label={t(col.labelKey)}
                sortKey={col.sortKey}
                currentSortField={sortField}
                currentSortOrder={sortOrder}
                onSort={onSort}
                align={col.align}
              />
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.map((job) => (
            <SyncJobRow
              key={job.id}
              job={job}
              isSelected={selectedRowId === String(job.id)}
              onRowClick={onRowClick}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export type { SortField, SortOrder }
